import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  countriesAPI,
  type Country,
  type CountryCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

export default function CountryPage() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CountryCreatePayload>({
    name: "",
    code: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<Country | null>(null);
  const [deleting, setDeleting] = useState<Country | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await countriesAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setCountries(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load countries");
      setCountries([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Country name is required");
      return;
    }
    setSubmitLoading(true);
    try {
      if (editing) {
        await countriesAPI.update(editing.id, {
          name: form.name.trim(),
          code: form.code?.trim() ?? undefined,
          is_active: form.is_active,
        });
        toast.success("Country updated successfully");
      } else {
        await countriesAPI.create({
          name: form.name.trim(),
          code: form.code?.trim() ?? undefined,
          is_active: form.is_active,
        });
        toast.success("Country created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", code: "", is_active: true });
      setEditing(null);
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? ((
              err as {
                response?: { data?: { message?: string; detail?: string } };
              }
            ).response?.data?.message ??
            (err as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail)
          : ((err as Error)?.message ?? "Failed to save country");
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const openEdit = useCallback((c: Country) => {
    setEditing(c);
    setForm({
      name: c.name,
      code: c.code ?? "",
      is_active: c.is_active ?? true,
    });
    setDialogOpen(true);
  }, []);

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<Country>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
      }),
    [currentPage, pageSize, openEdit]
  );

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await countriesAPI.delete(deleting.id);
      toast.success("Country deleted successfully");
      setDeleting(null);
      if (countries.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete country";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Country</h1>
        <p className="text-muted-foreground">Manage countries</p>
      </div>

      <DataTableProvider tableId="list-master-country" columns={tableColumns}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
            <CardTitle>Countries ({totalItems})</CardTitle>
            <div className="flex items-center gap-2">
              <Dialog
                open={dialogOpen}
                onOpenChange={open => {
                  setDialogOpen(open);
                  if (!open) {
                    setForm({ name: "", code: "", is_active: true });
                    setEditing(null);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    onClick={() => {
                      setForm({ name: "", code: "", is_active: true });
                      setEditing(null);
                      setDialogOpen(true);
                    }}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Create new
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editing ? "Edit Country" : "Create New Country"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="country-name">Name *</Label>
                      <Input
                        id="country-name"
                        value={form.name}
                        onChange={e =>
                          setForm(prev => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Country name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country-code">Code</Label>
                      <Input
                        id="country-code"
                        value={form.code ?? ""}
                        onChange={e =>
                          setForm(prev => ({ ...prev, code: e.target.value }))
                        }
                        placeholder="e.g. IN"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="country-active"
                        checked={form.is_active ?? true}
                        onCheckedChange={c =>
                          setForm(prev => ({ ...prev, is_active: c }))
                        }
                      />
                      <Label htmlFor="country-active">Active</Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        disabled={submitLoading}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleSave} disabled={submitLoading}>
                        {submitLoading ? (
                          <CustomLoader size="xs" />
                        ) : editing ? (
                          "Update"
                        ) : (
                          "Create"
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <DataTableColumnSettings columns={tableColumns} />
            </div>
          </CardHeader>
          <CardContent className="space-y-3 p-3 pt-0">
            {(totalItems > 0 || searchTerm.trim() !== "") && (
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="relative w-full md:max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={e => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10"
                  />
                </div>
              </div>
            )}
            {loading ? (
              <div className="flex h-64 items-center justify-center">
                <CustomLoader size="md" />
              </div>
            ) : (
              <DataTable
                embedded
                hideToolbar
                tableId="list-master-country"
                columns={tableColumns}
                data={countries}
                getRowKey={c => c.id}
                emptyMessage={
                  totalItems === 0 && !searchTerm.trim()
                    ? "No countries yet. Create one to get started."
                    : "No countries found matching your search."
                }
              />
            )}

            {totalItems > 0 && (
              <div className="mt-4 border-t pt-4">
                <CommonPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalCount={totalItems}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={size => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </DataTableProvider>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Country</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
